/**
 * Does a text point off the app — a social handle, a platform, a link, an
 * address or a phone number? A conservative panel-side mirror of shuffly's
 * bio screen (`app/Support/TextScreening/ContactHandleDetector.php` and
 * `PhoneNumberDetector.php`), so generated bios never carry one. shuffly stays
 * authoritative: the panel also asks it before commit.
 *
 * Erring toward "yes" is fine here. A false positive only drops a generated
 * line; a false negative would put an Instagram handle on a synthetic profile,
 * which the real app refuses for its own users.
 */

const NAMES_ANYWHERE = [
  'instagram', 'instagrm', 'instgram', 'istagram', 'insatgram', 'instegram', 'instaqram',
  'telegram', 'telegrm', 'telgram', 'teleqram',
  'snapchat', 'whatsapp', 'whatapp', 'watsap', 'vatsap', 'vatsab',
  'facebook', 'feysbuk', 'messenger', 'onlyfans', 'tiktok', 'linktree', 'tellonym',
  'discord', 'diskord', 'twitter', 'twiter', 'hotmail', 'youtube',
];

const NAME_PREFIXES = ['skype', 'viber', 'wechat', 'tinder', 'bumble', 'gmail', 'hotmail', 'outlook'];

const SHORT_NAME = /^(?:insta|snap|tiktok|tg|dm)(?:m|im|um|mi|miz|mizi|mda|mdan|da|dan|ya|yi|dayim|tan|ta)?$/;

const LINK_PATTERNS = [
  /h(?:tt|xx)ps?:|www\./,
  /(?<![a-z0-9])(?:t\.me|wa\.me|m\.me|fb\.me|ig\.me|bit\.ly|t\.ly|linktr\.ee|discord\.gg|instagr\.am|youtu\.be|goo\.gl|vk\.cc)(?![a-z0-9])/,
  /[a-z0-9]\s*[[({]?\s*\.\s*[\])}]?\s*(?:com|org|xyz|net)(?![a-z0-9])/,
  /[a-z0-9]\.(?:io|ly|gg|tv|ru|uk|de|fr|tr|me)(?![a-z0-9])/,
  /[a-z0-9]\s*[[({]?\s*(?:dot|nokta|punkt|point)\s*[\])}]?\s*(?:com|org)(?![a-z0-9])/,
];

const AT = /@|[([{]\s*at\s*[)\]}]/;

/** "ig ahmet_34", "sc: ahmet" — an abbreviation is a platform only with a handle after it. */
const ABBREVIATION =
  /(?<![a-z0-9])(?:ig|tg|sc|wp|wa|dc|fb|tt|kik|vk|bip|tlg|ins|inst|tele|face|signal|snap|insta)(?![a-z0-9])([^a-z0-9]*)([a-z0-9][a-z0-9._]{2,29})/g;

/** Seven or more digits written as one number, however it is spaced. */
const PHONE = /(?<![0-9])\+?[0-9](?:[\s().-]*[0-9]){6,}/;

export function containsExternalContact(text: string): boolean {
  const folded = fold(text);
  if (AT.test(folded)) return true;
  if (LINK_PATTERNS.some((p) => p.test(folded))) return true;
  if (PHONE.test(folded)) return true;

  const squeezed = folded.replace(/[^a-z0-9]/g, '');
  if (NAMES_ANYWHERE.some((n) => squeezed.includes(n))) return true;

  const tokens = folded.split(/[^a-z0-9]+/).filter((t) => t !== '');
  for (const token of tokens) {
    const collapsed = token.replace(/([a-z])\1+/g, '$1');
    if (SHORT_NAME.test(token) || SHORT_NAME.test(collapsed)) return true;
    if (NAME_PREFIXES.some((p) => token.startsWith(p))) return true;
  }

  for (const [, separator = '', handle = ''] of folded.matchAll(ABBREVIATION)) {
    if (announcesHandle(separator, handle)) return true;
  }
  return false;
}

/** "sc: ahmet" announces by what stands between; "ig ahmet_34" by the handle's shape. */
function announcesHandle(separator: string, handle: string): boolean {
  if (separator.replace(/[\s_.]/g, '') !== '') return true;
  return /[a-z]/.test(handle) && /[0-9_]|[a-z0-9]\.[a-z0-9]/.test(handle);
}

/** Lowercase, Turkish dotless i folded, diacritics and ß stripped. */
function fold(text: string): string {
  return text
    .replace(/İ/g, 'i')
    .replace(/I/g, 'i')
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}
